import { useCallback, useMemo, useRef, useState } from 'react';

import { AdminPermissions } from 'constants/admin-permissions';
import {
  ADMIN_CHECK_TYPE_MULTI_SELECT_NORMALIZE,
  ALL_TYPES_FILTER_NAME,
  ALL_TYPES_FILTER_VALUE,
  SUPPORT_CHECKS_FILTER_VALUE,
  buildCheckTypeFilterOptions,
  isTruthyDbBool,
} from 'helpers/check-type-filter-options';
import { useAccessControl } from 'hooks/access-control';
import { useDocumentTitle } from 'hooks/document-title';
import { useSupportUserNames } from 'hooks/use-support-user-names';
import AdminPage from 'layouts/AdminPage/AdminPage';
import { useSelector } from 'react-redux';

import { Button } from 'components/FormComponents';
import { Table, useTableContext } from 'components/Table/Table';

import { selectAuthentication } from 'api/authentication-slice';
import {
  useGetCheckGroupsQuery,
  useGetCheckTypesQuery,
  useLazyGetCheckTypesListQuery,
  useUpdateDefaultSupportUserMutation,
} from 'api/check-types-api';

import { ActiveViewBanner } from '../../Reporting/components/SavedViews/FiltersChangedBanner';
import { ReportingSavedViewsTableSync } from '../../Reporting/components/SavedViews/ReportingSavedViewsIntegrations';
import { SavedViewsDropdown } from '../../Reporting/components/SavedViews/SavedViewsDropdown';
import { useSavedViews } from '../../Reporting/components/SavedViews/useSavedViews';

const PERIOD_OPTIONS = [
  { name: '1 month', value: '1' },
  { name: '3 months', value: '3' },
  { name: '6 months', value: '6' },
  { name: '12 months', value: '12' },
];

const COUNTRY_OPTIONS = [
  { name: 'ALL', value: 'all' },
  { name: 'INT', value: 'INT' },
  { name: 'NZ', value: 'NZ' },
  { name: 'AU', value: 'AU' },
  { name: 'US', value: 'US' },
  { name: 'UK', value: 'UK' },
];

const HAS_SUBCHECKS_OPTIONS = [
  { name: 'ALL', value: 'all' },
  { name: 'Has Subchecks', value: 'yes' },
  { name: 'No Subchecks', value: 'no' },
];

const DEPRECATED_OPTIONS = [
  { name: 'Exclude Deprecated', value: 'exclude' },
  { name: 'Include Deprecated', value: 'include' },
  { name: 'Deprecated Only', value: 'only' },
];

/** Only timeframe (months int, or date_range with from/to) is sent to the list API. */
const buildListParams = params => {
  const timeframe = params?.timeframe?.value ?? params?.timeframe ?? 12;
  const out = { timeframe: Number(timeframe) || 12 };
  if (timeframe === 'date_range' && params?.from?.value && params?.to?.value) {
    out.timeframe = 'date_range';
    out.from = params.from.value;
    out.to = params.to.value;
  } else if (timeframe === 'date_range' && params?.from && params?.to) {
    out.timeframe = 'date_range';
    out.from = params.from;
    out.to = params.to;
  }
  return out;
};

const ApplyFiltersButton = () => {
  const { manuallyApplyFilters } = useTableContext();
  return (
    <Button icon="filter" onClick={manuallyApplyFilters}>
      Apply Filters
    </Button>
  );
};

const formatCompleted = value => {
  const n = typeof value === 'number' && !Number.isNaN(value) ? value : 0;
  return n.toLocaleString();
};

const formatPercent = value => {
  const n = typeof value === 'number' && !Number.isNaN(value) ? value : 0;
  return `${Number(n).toFixed(1)}%`;
};

const humanizeReportClass = value => {
  if (value == null || value === '') return '';
  return String(value)
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, s => s.toUpperCase())
    .trim();
};

const hasValue = (val, checkArray = false) => {
  if (val == null) return false;
  if (checkArray) {
    let arr = val;
    if (typeof val === 'string') {
      try {
        arr = JSON.parse(val || '[]');
      } catch {
        return false;
      }
    }
    return Array.isArray(arr) ? arr.length > 0 : false;
  }
  if (typeof val === 'string') return val.trim() !== '';
  if (typeof val === 'object') return Object.keys(val).length > 0;
  return true;
};

const CheckTypesTableBody = ({
  tableRows,
  canUpdateCheck,
  supportUsers,
  onUpdateDefaultSupportUser,
  updatingCheckTypeSlug,
}) => {
  const { queryParams } = useTableContext();
  const searchTerm = (queryParams.search?.value ?? '').toLowerCase().trim();
  const filtered = !searchTerm
    ? tableRows
    : tableRows.filter(
        row =>
          (row.title ?? '').toLowerCase().includes(searchTerm) ||
          (row.slug ?? '').toLowerCase().includes(searchTerm) ||
          (row.check_group_title ?? '').toLowerCase().includes(searchTerm) ||
          (row.default_support_user_name ?? '').toLowerCase().includes(searchTerm),
      );

  return (
    <>
      {filtered.map(row => (
        <Table.Row key={row.slug}>
          <Table.Col>{row.title}</Table.Col>
          <Table.Col>{row.check_group_title ?? '-'}</Table.Col>
          <Table.Col>{formatCompleted(row.completed_count)}</Table.Col>
          <Table.Col>{formatPercent(row.percent_of_total)}</Table.Col>
          <Table.Col>
            {typeof row.avg_hours_to_complete === 'number' &&
            !Number.isNaN(row.avg_hours_to_complete)
              ? row.avg_hours_to_complete
              : '-'}
          </Table.Col>
          {canUpdateCheck && (
            <Table.Col>
              <select
                className="form-control form-control-sm"
                value={row.default_support_user_id ?? ''}
                onChange={e =>
                  onUpdateDefaultSupportUser(
                    row.slug,
                    e.target.value ? Number(e.target.value) : null,
                  )
                }
                disabled={updatingCheckTypeSlug === row.slug}
                aria-label={`Default support user for ${row.title}`}>
                <option value="">—</option>
                {/* Show current assignee in the box even if not in the dropdown list */}
                {row.default_support_user_id &&
                  row.default_support_user_name &&
                  !(supportUsers || []).some(u => u.id === row.default_support_user_id) && (
                    <option value={row.default_support_user_id}>
                      {row.default_support_user_name}
                    </option>
                  )}
                {(supportUsers || []).map(user => (
                  <option key={user.id} value={user.id}>
                    {[user.first_name, user.last_name].filter(Boolean).join(' ')}
                  </option>
                ))}
              </select>
            </Table.Col>
          )}
        </Table.Row>
      ))}
    </>
  );
};

const SortableHeader = ({ column, label, sortBy, sortDir, onSort }) => {
  const active = sortBy === column;
  const arrow = active ? (sortDir === 'desc' ? ' ↓' : ' ↑') : '';
  return (
    <Table.Header key={column}>
      <span
        role="button"
        tabIndex={0}
        onClick={() => onSort(column)}
        onKeyDown={e => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onSort(column);
          }
        }}
        style={{ cursor: 'pointer', userSelect: 'none' }}
        title={`Sort by ${label}${active ? ` (${sortDir === 'desc' ? 'descending' : 'ascending'})` : ''}`}>
        {label}
        {arrow}
      </span>
    </Table.Header>
  );
};

const AdminCheckTypes = () => {
  useDocumentTitle('Check Types');

  const [checkTypes, setCheckTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tableQuery, setTableQuery] = useState({});
  const [sortBy, setSortBy] = useState('title');
  const [sortDir, setSortDir] = useState('asc');

  const authentication = useSelector(selectAuthentication);
  const hasPermission = useAccessControl(authentication.admin_permissions);
  const canUpdateCheck = hasPermission(AdminPermissions.UpdateCheckType);
  const { supportUsers } = useSupportUserNames();

  const savedViewsState = useSavedViews('admin_check_types');

  const handleSavedViewSelect = (id, params) => {
    savedViewsState.setActiveViewId(id);
    savedViewsState.setPendingApply({ params, key: Date.now() });
  };

  const activeSavedView = savedViewsState.savedViews.find(
    v => Number(v.id) === Number(savedViewsState.activeViewId),
  );
  const savedViewBanner = activeSavedView ? (
    <ActiveViewBanner
      viewName={activeSavedView.name}
      activeViewData={activeSavedView}
      bannerViewQuerySynced={savedViewsState.bannerViewQuerySynced}
      onClearView={() => {
        savedViewsState.setActiveViewId(null);
        savedViewsState.setPendingApply(null);
      }}
      onRestore={() => {}}
      onUpdate={savedViewsState.handleUpdate}
      onSaveNew={() => savedViewsState.setSaveViewModal(true)}
      onSetDefault={savedViewsState.handleSetDefault}
    />
  ) : null;

  const { data: checkGroups = [] } = useGetCheckGroupsQuery();
  const { data: allCheckTypes = [] } = useGetCheckTypesQuery();
  const [getCheckTypesList] = useLazyGetCheckTypesListQuery();
  const [updateDefaultSupportUser] = useUpdateDefaultSupportUserMutation();
  const [updatingCheckTypeSlug, setUpdatingCheckTypeSlug] = useState(null);
  const lastFetchParamsRef = useRef(null);

  const handleUpdateDefaultSupportUser = useCallback(
    (checkTypeSlug, userId) => {
      setUpdatingCheckTypeSlug(checkTypeSlug);
      updateDefaultSupportUser({ slug: checkTypeSlug, default_support_user_id: userId })
        .unwrap()
        .then(result =>
          setCheckTypes(prev =>
            prev.map(ct =>
              ct.slug === result.slug
                ? {
                    ...ct,
                    default_support_user_id: result.default_support_user_id,
                    default_support_user_name: result.default_support_user_name,
                  }
                : ct,
            ),
          ),
        )
        .catch(err => {
          const message =
            err?.data?.message ||
            err?.message ||
            (typeof err === 'string' ? err : 'Failed to update default support user');
          console.error('Update default support user:', message, err);
          // Optionally show toast: e.g. toast.error(message);
        })
        .finally(() => setUpdatingCheckTypeSlug(null));
    },
    [updateDefaultSupportUser],
  );

  const checkGroupOptions = useMemo(
    () => [
      { name: 'All Groups', value: 'all' },
      ...checkGroups.map(opt => ({ name: opt.name ?? opt.value, value: opt.value })),
    ],
    [checkGroups],
  );

  const checkTypeOptions = useMemo(
    () =>
      buildCheckTypeFilterOptions(allCheckTypes, {
        includeSupportChecks: true,
        excludeDeprecated: true,
      }),
    [allCheckTypes],
  );

  const fetchList = useCallback(
    async listParams => {
      setLoading(true);
      try {
        const typesResult = await getCheckTypesList(listParams).unwrap();
        setCheckTypes(Array.isArray(typesResult) ? typesResult : []);
      } catch (e) {
        setCheckTypes([]);
      } finally {
        setLoading(false);
      }
    },
    [getCheckTypesList],
  );

  // Rely on Table.Context's effect to call onQueryUpdate on mount (single fetch). Refetch only when timeframe (or from/to) changes.
  const onQueryUpdate = useCallback(
    (flatParams, fullQueryParams) => {
      const params = fullQueryParams ?? flatParams;
      setTableQuery(params);
      const nextListParams = buildListParams(params);
      const prev = lastFetchParamsRef.current;
      const same =
        prev &&
        nextListParams.timeframe === prev.timeframe &&
        nextListParams.from === prev.from &&
        nextListParams.to === prev.to;
      if (!same) {
        lastFetchParamsRef.current = nextListParams;
        fetchList(nextListParams);
      }
    },
    [fetchList],
  );

  // Frontend filters: apply check_group, check_type, country, has_subchecks, include_deprecated to full list (memoized).
  const tableRows = useMemo(() => {
    if (!checkTypes.length) return [];
    const q = tableQuery || {};
    const getVal = key => {
      const v = q[key];
      const val = v != null && typeof v === 'object' && 'value' in v ? v.value : v;
      return Array.isArray(val) ? val : val != null && val !== '' ? [val] : [];
    };
    const checkGroups = getVal('check_group').filter(x => x !== 'all');
    const checkTypesFilter = getVal('check_type').filter(
      x => x !== 'all' && x !== SUPPORT_CHECKS_FILTER_VALUE,
    );
    const supportOnly = getVal('check_type').includes(SUPPORT_CHECKS_FILTER_VALUE);
    const countries = getVal('country').filter(x => x !== 'all');
    const hasSubchecks = q.has_subchecks?.value ?? q.has_subchecks;
    const deprecatedFilter = q.include_deprecated?.value ?? q.include_deprecated ?? 'exclude';

    let rows = checkTypes.map(ct => ({
      ...ct,
      check_group_title: ct.check_group_title ?? ct.check_group ?? '-',
      completed_count: ct.completed_count != null ? Number(ct.completed_count) : 0,
      percent_of_total: ct.percent_of_total != null ? Number(ct.percent_of_total) : 0,
      avg_hours_to_complete:
        ct.avg_hours_to_complete != null && ct.avg_hours_to_complete !== ''
          ? Number(ct.avg_hours_to_complete)
          : null,
    }));

    if (checkGroups.length > 0) rows = rows.filter(r => checkGroups.includes(r.check_group));
    if (checkTypesFilter.length > 0) rows = rows.filter(r => checkTypesFilter.includes(r.slug));
    if (supportOnly) rows = rows.filter(r => isTruthyDbBool(r.is_manual));
    if (countries.length > 0) {
      rows = rows.filter(r => {
        const ca = r.countries_available;
        let arr = [];
        if (Array.isArray(ca)) arr = ca;
        else if (typeof ca === 'string') {
          try {
            const parsed = JSON.parse(ca || '[]');
            arr = Array.isArray(parsed) ? parsed : [];
          } catch {
            arr = [];
          }
        }
        return countries.some(c => arr.includes(c));
      });
    }
    if (hasSubchecks === 'yes') rows = rows.filter(r => !!r.has_subchecks);
    else if (hasSubchecks === 'no') rows = rows.filter(r => !r.has_subchecks);
    if (deprecatedFilter === 'only') rows = rows.filter(r => isTruthyDbBool(r.deprecated));
    else if (deprecatedFilter === 'exclude' || deprecatedFilter === 'false')
      rows = rows.filter(r => !isTruthyDbBool(r.deprecated));

    const mult = sortDir === 'asc' ? 1 : -1;
    rows.sort((a, b) => {
      if (sortBy === 'title') {
        const cmp = (a.title ?? '').localeCompare(b.title ?? '');
        if (cmp !== 0) return mult * cmp;
      } else if (sortBy === 'check_group') {
        const cmp = (a.check_group_title ?? '').localeCompare(b.check_group_title ?? '');
        if (cmp !== 0) return mult * cmp;
      } else if (sortBy === 'completed') {
        const ca = a.completed_count ?? 0;
        const cb = b.completed_count ?? 0;
        if (ca !== cb) return mult * (ca - cb);
      } else if (sortBy === 'percent') {
        const pa = a.percent_of_total ?? 0;
        const pb = b.percent_of_total ?? 0;
        if (pa !== pb) return mult * (pa - pb);
      } else if (sortBy === 'avg_hours') {
        const ha = a.avg_hours_to_complete;
        const hb = b.avg_hours_to_complete;
        const na = typeof ha === 'number' && !Number.isNaN(ha) ? ha : -1;
        const nb = typeof hb === 'number' && !Number.isNaN(hb) ? hb : -1;
        if (na !== nb) return mult * (na - nb);
      }
      return (a.title ?? '').localeCompare(b.title ?? '');
    });
    return rows;
  }, [checkTypes, tableQuery, sortBy, sortDir]);

  const handleSort = useCallback(
    column => {
      if (sortBy === column) {
        setSortDir(prev => (prev === 'desc' ? 'asc' : 'desc'));
        return;
      }
      setSortBy(column);
      setSortDir(column === 'title' || column === 'check_group' ? 'asc' : 'desc');
    },
    [sortBy],
  );

  const handleDownloadCsv = useCallback(() => {
    const headers = [
      'Title',
      'Check Group',
      'slug',
      'No completed',
      '%',
      'Average Hrs',
      'Countries Available',
      'Is Support Check',
      'Has Subchecks',
      'Pricing Mode',
      'Result Flag True',
      'Result Flag False',
      'Has Expiry',
      'Has Report',
      'Report Type',
      'Has ID Verification',
      'Has Signature',
      'Has FCRA Disclosures',
      'Provider Follow Up Period',
      ...(canUpdateCheck ? ['Default Support User'] : []),
      'Has Configuration',
      'Has Client Fields',
      'Deprecated',
    ];
    const escape = v => {
      const s = v == null ? '' : String(v);
      return s.includes(',') || s.includes('"') || s.includes('\n')
        ? `"${s.replace(/"/g, '""')}"`
        : s;
    };
    // API may return MySQL TINYINT as 0/1; use truthy so CSV shows Yes/No correctly
    const boolYesNo = v => (isTruthyDbBool(v) ? 'Yes' : 'No');
    const csvRows = [
      headers.join(','),
      ...tableRows.map(r => {
        const hasConfig = (() => {
          const c = r.configuration;
          if (c == null) return false;
          if (Array.isArray(c)) return c.length > 0;
          if (typeof c === 'object') return Object.keys(c).length > 0;
          if (typeof c === 'string') {
            try {
              const parsed = JSON.parse(c);
              return Array.isArray(parsed)
                ? parsed.length > 0
                : Object.keys(parsed || {}).length > 0;
            } catch {
              return c.trim() !== '';
            }
          }
          return true;
        })();
        const hasClientFields = hasValue(r.client_fields, true);
        let countriesList = '';
        if (Array.isArray(r.countries_available)) {
          countriesList = r.countries_available.join(', ');
        } else if (typeof r.countries_available === 'string') {
          try {
            const parsed = JSON.parse(r.countries_available);
            countriesList = Array.isArray(parsed) ? parsed.join(', ') : r.countries_available;
          } catch {
            countriesList = r.countries_available;
          }
        }
        // Export % as decimal (0.25) so CSV is a proper percentage; API sends 0–100, round to 3 dp
        const pctRaw =
          typeof r.percent_of_total === 'number' && !Number.isNaN(r.percent_of_total)
            ? r.percent_of_total / 100
            : 0;
        const pct = Math.round(pctRaw * 1000) / 1000;
        return [
          escape(r.title ?? ''),
          escape(r.check_group_title ?? r.check_group ?? ''),
          escape(r.slug ?? ''),
          r.completed_count ?? 0,
          pct,
          r.avg_hours_to_complete ?? '',
          escape(countriesList),
          boolYesNo(r.is_manual),
          boolYesNo(r.has_subchecks),
          escape(r.pricing_mode ?? ''),
          escape(r.true_flag_label ?? ''),
          escape(r.false_flag_label ?? ''),
          boolYesNo(r.has_expiration_date),
          boolYesNo(r.has_report),
          escape(humanizeReportClass(r.report_class)),
          boolYesNo(r.has_id_requirements),
          boolYesNo(r.signature_required),
          boolYesNo(r.has_fcra),
          r.provider_follow_up_period ?? '',
          ...(canUpdateCheck ? [escape(r.default_support_user_name ?? '')] : []),
          hasConfig ? 'Yes' : 'No',
          hasClientFields ? 'Yes' : 'No',
          boolYesNo(r.deprecated),
        ].join(',');
      }),
    ];
    const blob = new Blob([csvRows.join('\n')], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'check_types_export.csv';
    a.click();
    URL.revokeObjectURL(url);
  }, [tableRows, canUpdateCheck]);

  const defaultQuery = useMemo(
    () => ({
      page: { value: 1 },
      timeframe: {
        value: '12',
        name: '12 months',
      },
      check_group: {
        value: [],
        name: [],
        type: 'status',
        title: 'Check Group',
      },
      check_type: {
        value: [ALL_TYPES_FILTER_VALUE],
        name: [ALL_TYPES_FILTER_NAME],
        type: 'status',
        title: 'Check Type',
      },
      country: {
        value: ['all'],
        name: ['ALL'],
        type: 'status',
        title: 'Country',
      },
      has_subchecks: {
        value: 'all',
        name: 'ALL',
        title: 'Subchecks',
      },
      include_deprecated: {
        value: 'exclude',
        name: 'Exclude Deprecated',
      },
    }),
    [],
  );

  return (
    <AdminPage loading={loading} title="Check Types" allowedRole={AdminPermissions.ListCheckTypes}>
      <Table.Context
        defaultQuery={defaultQuery}
        manualQueryUpdate={true}
        suppressQueryParamEffect={!savedViewsState.savedViewsReady}
        onQueryUpdate={onQueryUpdate}
        multiSelectNormalize={{
          check_group: { allValue: 'all', allName: 'All Groups' },
          check_type: ADMIN_CHECK_TYPE_MULTI_SELECT_NORMALIZE,
          country: { allValue: 'all', allName: 'ALL' },
        }}>
        <ReportingSavedViewsTableSync tab="admin_check_types" savedViewsState={savedViewsState} />
        {savedViewsState.savedViews.length > 0 && (
          <div className="d-flex justify-content-end u-margin--large u-margin-bottom--small">
            <SavedViewsDropdown
              savedViews={savedViewsState.savedViews}
              activeViewId={savedViewsState.activeViewId}
              onSelectView={handleSavedViewSelect}
              onSetDefault={savedViewsState.handleSetDefault}
              onManage={() => savedViewsState.setManageViewsOpen(true)}
              tab="admin_check_types"
            />
          </div>
        )}
        <Table.FloatingFilters
          className="u-margin--large surface-white"
          banner={savedViewBanner}
          actions={
            <Button
              type="secondary"
              icon="bookmark"
              onClick={() => savedViewsState.setSaveViewModal(true)}>
              Save View
            </Button>
          }>
          <Table.FloatingFiltersLeft className="u-gap-medium align-items-end">
            <Table.Filter
              title="Period"
              filterName="timeframe"
              options={PERIOD_OPTIONS}
              useDefault={false}
            />
            <Table.MultiSelectFilter
              title="Check Group"
              filterName="check_group"
              type="status"
              options={checkGroupOptions}
              useDefault={false}
              useSearchInMultiSelect={true}
            />
            <Table.MultiSelectFilter
              title="Check Type"
              filterName="check_type"
              type="status"
              options={checkTypeOptions}
              useSearchInMultiSelect={true}
            />
            <Table.MultiSelectFilter
              title="Country"
              filterName="country"
              type="status"
              options={COUNTRY_OPTIONS}
              useDefault={false}
              useSearchInMultiSelect={true}
            />
            <Table.Filter
              title="Subchecks"
              filterName="has_subchecks"
              options={HAS_SUBCHECKS_OPTIONS}
              useDefault={false}
            />
            <Table.Filter
              title="Deprecated"
              filterName="include_deprecated"
              options={DEPRECATED_OPTIONS}
              useDefault={false}
            />
          </Table.FloatingFiltersLeft>
          <Table.FloatingFiltersRight>
            <ApplyFiltersButton />
          </Table.FloatingFiltersRight>
        </Table.FloatingFilters>
        <Table.TableContent className="u-margin--large">
          <Table.Filters>
            <Table.LeftFilters>
              <Table.Search placeholder="Search title, slug, group, or support user..." />
            </Table.LeftFilters>
            <Table.RightFilters>
              <Button icon="download" onClick={handleDownloadCsv}>
                Download CSV
              </Button>
            </Table.RightFilters>
          </Table.Filters>
          <Table className="u-margin--large">
            <Table.Head sticky>
              <Table.Row key="header-row">
                <SortableHeader
                  key="title"
                  column="title"
                  label="Title"
                  sortBy={sortBy}
                  sortDir={sortDir}
                  onSort={handleSort}
                />
                <SortableHeader
                  key="check_group"
                  column="check_group"
                  label="Check Group"
                  sortBy={sortBy}
                  sortDir={sortDir}
                  onSort={handleSort}
                />
                <SortableHeader
                  key="completed"
                  column="completed"
                  label="Completed"
                  sortBy={sortBy}
                  sortDir={sortDir}
                  onSort={handleSort}
                />
                <SortableHeader
                  key="percent"
                  column="percent"
                  label="% of Total"
                  sortBy={sortBy}
                  sortDir={sortDir}
                  onSort={handleSort}
                />
                <SortableHeader
                  key="avg_hours"
                  column="avg_hours"
                  label="Average Hrs to Complete"
                  sortBy={sortBy}
                  sortDir={sortDir}
                  onSort={handleSort}
                />
                {canUpdateCheck && (
                  <Table.Header key="default_support_user">Default Support User</Table.Header>
                )}
              </Table.Row>
            </Table.Head>
            <Table.Body>
              <CheckTypesTableBody
                tableRows={tableRows}
                canUpdateCheck={canUpdateCheck}
                supportUsers={supportUsers}
                onUpdateDefaultSupportUser={handleUpdateDefaultSupportUser}
                updatingCheckTypeSlug={updatingCheckTypeSlug}
              />
            </Table.Body>
          </Table>
          {tableRows.length === 0 && !loading && (
            <p className="secondary-text u-padding--large">No check types match the filters.</p>
          )}
        </Table.TableContent>
      </Table.Context>
    </AdminPage>
  );
};

export default AdminCheckTypes;
